/**
 * ChangeLogNotes — reusable Change Log + Notes panel used inside Insurance,
 * Subcontractor Insurance, and EIN row modals. Follows the exact visual
 * language of the equivalent panels in `LoanModal.jsx`.
 *
 * Props:
 *   basePath       — e.g. "/insurance/xyz" or "/subcontractors/vid" or "/ein/eid"
 *   idFieldLabel   — plain-english label used when formatting the "__created__"
 *                    row: "Created record #1002"
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClockCounterClockwise, PaperPlaneRight, Trash, PencilSimple, Check, X } from "@phosphor-icons/react";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}
function formatVal(v) {
  if (v == null || v === "") return "∅";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function ChangeLogNotes({ basePath, idFieldLabel = "record" }) {
  const { user } = useAuth();
  const [log, setLog] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, n] = await Promise.all([
        api.get(`${basePath}/log`),
        api.get(`${basePath}/notes`),
      ]);
      setLog(l.data || []);
      setNotes(n.data || []);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  }, [basePath]);

  useEffect(() => { if (basePath) load(); }, [basePath, load]);

  const postNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setPosting(true);
    try {
      const { data } = await api.post(`${basePath}/notes`, { body });
      setNotes((prev) => [data, ...prev]);
      setNoteBody("");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setPosting(false); }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`${basePath}/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditBody(n.body || ""); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };
  const saveEdit = async (n) => {
    const body = editBody.trim();
    if (!body) { toast.error("Note cannot be empty"); return; }
    if (body === (n.body || "").trim()) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`${basePath}/notes/${n.id}`, { body });
      setNotes((prev) => prev.map((x) => x.id === n.id ? data : x));
      cancelEdit();
      toast.success("Note updated");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
          <ClockCounterClockwise size={11} /> Change log
        </div>
        <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "calc(80vh / 3)" }}>
          {log.length === 0 && <div className="text-[10px] text-slate-400 italic">No changes yet.</div>}
          {log.map((l) => (
            <div key={l.id} className="text-[10px] border-l-2 border-slate-200 pl-2">
              <div className="text-slate-500">{fmtTime(l.changed_at)} · <span className="font-semibold text-slate-700">{l.changed_by_name}</span></div>
              <div className="text-slate-800">
                {l.field === "__created__" ? (
                  <>Created {idFieldLabel} #{l.new_value}</>
                ) : l.field.startsWith("attachment.") ? (
                  l.new_value ? (
                    <><span className="font-semibold">+ document</span> {l.field.replace("attachment.", "")}: {l.new_value.filename} <span className="text-slate-400">(v{l.new_value.version})</span></>
                  ) : (
                    <><span className="font-semibold text-rose-600">− document</span> {l.field.replace("attachment.", "")}: {l.old_value?.filename} <span className="text-slate-400">(v{l.old_value?.version})</span></>
                  )
                ) : (
                  <>Changed <span className="font-semibold">{l.field}</span> from <code className="text-slate-500">{formatVal(l.old_value)}</code> to <code className="text-[#1f64c2]">{formatVal(l.new_value)}</code></>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Notes timeline</div>
        <div className="flex gap-1.5 mb-2">
          <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
            placeholder={`Add a note about this ${idFieldLabel}…`} rows={2}
            className="resize-none text-xs" data-testid={`notes-input-${basePath.replace(/[^a-z0-9]/gi, "-")}`} />
          <Button size="sm" onClick={postNote} disabled={posting || !noteBody.trim()}
            className="bg-[#327de3] hover:bg-[#1f64c2] self-end" data-testid={`notes-post-${basePath.replace(/[^a-z0-9]/gi, "-")}`}>
            <PaperPlaneRight size={12} />
          </Button>
        </div>
        <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "calc(80vh * 2 / 3)" }}>
          {notes.length === 0 && <div className="text-[10px] text-slate-400 italic">No notes yet.</div>}
          {notes.map((n) => {
            const mine = n.author_id === user?.id;
            const canEdit = mine || user?.is_super_admin;
            const isEditing = editingId === n.id;
            const wasEdited = !!n.edited_at && n.edited_at !== n.created_at;
            return (
              <div key={n.id} className="text-[11px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
                <div className="flex items-center gap-1 text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700">{n.author_name}</span> · {fmtTime(n.created_at)}
                  {wasEdited && (
                    <span className="italic text-slate-400" title={`Edited ${fmtTime(n.edited_at)}${n.last_edited_by_name ? ` by ${n.last_edited_by_name}` : ""}`}>
                      · edited {fmtTime(n.edited_at)}
                    </span>
                  )}
                  {canEdit && !isEditing && (
                    <button onClick={() => startEdit(n)} className="ml-auto text-slate-500 hover:text-[#1f64c2]" title="Edit" data-testid={`note-edit-${n.id}`}><PencilSimple size={10} /></button>
                  )}
                  {(mine || user?.is_super_admin) && !isEditing && (
                    <button onClick={() => deleteNote(n)} className={`${canEdit ? "" : "ml-auto"} text-rose-500 hover:text-rose-700`} title="Delete"><Trash size={10} /></button>
                  )}
                </div>
                {isEditing ? (
                  <div className="mt-1 space-y-1">
                    <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2}
                      className="resize-none text-xs" autoFocus data-testid={`note-edit-input-${n.id}`} />
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={savingEdit} className="h-6 px-2 text-[10px]" data-testid={`note-edit-cancel-${n.id}`}><X size={10} className="mr-0.5" />Cancel</Button>
                      <Button size="sm" onClick={() => saveEdit(n)} disabled={savingEdit || !editBody.trim()} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid={`note-edit-save-${n.id}`}><Check size={10} className="mr-0.5" />{savingEdit ? "Saving…" : "Save"}</Button>
                    </div>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-slate-800">{n.body}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
